import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ComponentRef,
    computed,
    DestroyRef,
    inject,
    Input,
    input,
    OnChanges,
    OnDestroy,
    OnInit,
    signal,
    SimpleChanges,
    viewChild,
    viewChildren
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { forkJoin, skipWhile, take } from 'rxjs';

import { BaasAppState, BaasI18n } from '@lvflanders/bar';
import { _BaasWindow, BaasCoreStateService, BarCoreService, BarMeldingType } from '@lvflanders/bar-ng';

import { BaasLoading } from '../../baas-loading/baas-loading';
import { BarLink } from '../../bar-link/bar-link';
import { BarI18nPipe } from '../../i18n/bar-i18n.pipe';
import { BaasMeldingen } from '../../meldingen/baas-meldingen/baas-meldingen';
import { BaasToastMessageService } from '../../toast-messages/baas-toast-message-service';
import { BaasToastMessages } from '../../toast-messages/baas-toast-messages/baas-toast-messages';
import { BaasGlobalFooter } from '../baas-global-footer';
import { BaasGlobalHeader } from '../baas-global-header';
import { BarAppMenuItem } from '../bar-app-menu-item.type';
import { BarDeviceType } from '../bar-device-type.enum';
import { _BarInternalLayoutService } from '../bar-internal-layout.service';
import { BAR_LAYOUT_CONSTANTS } from '../bar-layout.constants';
import { BarLayoutInternalGvkService } from '../bar-layout-internal-gvk.service';
import { BarLayoutSection } from '../bar-layout-section.enum';
import { BarLayoutSectionInjection } from '../bar-layout-section-injection.model';
import { BarLayoutState } from '../bar-layout-state.contract';
import { BarResponsiveOptions } from '../bar-responsive-options.type';
import { _BarBurgerprofielEngine } from '../burgerprofiel/bar-burgerprofiel-engine-provider';
import { BAAS_LAYOUT_STATE } from '../layout-tokens';
import { BaasOffcanvas } from '../offcanvas/baas-offcanvas';
import { BaasOffcanvasOptions } from '../offcanvas/baas-offcanvas-options';
import { BarLayoutService } from './bar-layout.service';
import { _BarLayoutAnchorDirective } from './bar-layout-anchor.directive';
import { BarLayoutPageComponent } from './bar-layout-page/bar-layout-page.component';

@Component({
    selector: 'bar-layout',
    templateUrl: './bar-layout.html',
    host: { class: 'bar-layout-root' },
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BaasGlobalHeader,
        _BarLayoutAnchorDirective,
        BarLink,
        BaasMeldingen,
        BarLayoutPageComponent,
        BaasGlobalFooter,
        RouterOutlet,
        BaasLoading,
        BaasToastMessages,
        AsyncPipe,
        BarI18nPipe,
        BaasOffcanvas
    ]
})
/** @deprecated Use BaasLayout component with OnPush change detection. */
export class BarLayout implements OnChanges, OnInit, OnDestroy {
    readonly layout = inject(BarLayoutService);
    readonly #internalLayout = inject(_BarInternalLayoutService);
    readonly #core = inject(BarCoreService);
    readonly #coreState = inject(BaasCoreStateService, { optional: true });
    readonly #appState = inject(BaasAppState, { optional: true });
    readonly #router = inject(Router);
    readonly #translate = inject(BaasI18n);
    readonly #gvk = inject(BarLayoutInternalGvkService);
    readonly #burgerprofielEngine = inject(_BarBurgerprofielEngine);
    readonly #toastMessage = inject(BaasToastMessageService);
    readonly #breakpointObserver = inject(BreakpointObserver);
    readonly #destroyRef = inject(DestroyRef);
    readonly #layoutState = inject<BarLayoutState>(BAAS_LAYOUT_STATE);
    readonly #window = inject(_BaasWindow);
    // holds all the anchors in which to inject components as a QueryList
    readonly barLayoutAnchors = viewChildren(_BarLayoutAnchorDirective);
    readonly pageComponent = viewChild(BarLayoutPageComponent);
    // NOTE this controls the *ngIf meaning no external scripts will be invoked
    readonly globalHeaderFooter = input(true);
    @Input() appMenuItems: BarAppMenuItem[];
    readonly box = input(this.#internalLayout.box);
    readonly withRouting = input(true);
    /** @experimental */
    readonly responsive = input<BarResponsiveOptions>({ enabled: false });
    desktopAppMenuItems: BarAppMenuItem[];
    mobileAppMenuItems: BarAppMenuItem[];

    protected readonly offcanvasOpened = signal<boolean>(false);
    protected readonly offcanvasPinned = signal<boolean>(false);
    protected offcanvasOptions: BaasOffcanvasOptions;

    skipLink = { main: null, menu: null };
    title: string;
    subTitle: string;
    // NOTE vreemd probleem met change detection pesca, hiervoor signal
    readonly loading = signal<boolean>(false);
    sidebar: boolean;
    hidden: boolean;
    forceHideBox: boolean;
    mobileFriendly = false;
    touch = false;
    compact = false;

    readonly meldingen = computed(() => this.#coreState?.meldingen()?.filter((m) => m.meldingType != BarMeldingType.Banner) || []);

    menuOverlayOpen = false;

    #layoutInitialized = true;

    get env(): string {
        return this.#layoutState.environment;
    }

    get rootUrl(): string {
        return this.#layoutState.rootUrl;
    }

    get devOrTest(): string {
        return ['DEV', 'TST'].find((env) => env === this.env);
    }

    // NOTE this controls the display style property meaning external scripts are loaded even if not shown
    get showGlobalHeaderFooter() {
        return !this.compact && !this.touch;
    }

    ngOnInit(): void {
        this.#internalLayout.legacyConsumer = true;
        this.forceHideBox = (this.box() && this.#burgerprofielEngine.visible) || this.globalHeaderFooter() === false;

        this.layout.documentTitle$.next(
            this.#appState == null || this.#appState.basiscontext == null || this.#appState.basiscontext.zone == 'E'
                ? this.#translate.get('DOMAIN_BAR_LAYOUT.TITLE')
                : this.#appState.toepassing.naam
        );

        this.#internalLayout.init();
        this.layout.init();

        this.layout.hidden$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((hidden) => {
            this.#internalLayout.queueAction(() => {
                this.hidden = hidden;
                if (hidden) document.body.style.backgroundColor = 'unset';
                else document.body.removeAttribute('style');
            });
        });

        if (this.withRouting()) {
            this.#router.events.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((event) => {
                if (event instanceof NavigationEnd) {
                    // initial only executed once
                    if (this.#layoutInitialized) {
                        // short wait time to properly load header/footer
                        // run timeout outside angular (avoid change detection cycle)
                        this.#window.setTimeoutOutsideAngular(() => {
                            // hide app loader
                            this.#defineAppLoading(false, true);
                            this.#layoutInitialized = false;
                        });
                    }

                    // set up baselink, pathName is the url without origin and without hash, query,...
                    const baseLink = location.pathname;
                    this.skipLink.main = `${baseLink}#maincontent`;
                    this.skipLink.menu = `${baseLink}#mainmenu`;

                    // if Fragment is present, focus it (will scroll into view automatically)
                    if (event.urlAfterRedirects.indexOf('#') > -1) {
                        const fragment = event.urlAfterRedirects.substring(event.urlAfterRedirects.indexOf('#') + 1);
                        const element = document.getElementById(fragment);
                        // make sure tabindex (-1, 0) is present on the to be focussed element (otherwise it will not work);
                        if (element) element.focus();
                    }
                }
            });
        } else {
            // hide app loader
            this.#defineAppLoading(false, true);
            // set up baselink, pathName is the url without origin and without hash, query,...
            const baseLink = location.pathname;
            this.skipLink.main = `${baseLink}#maincontent`;
            this.skipLink.menu = `${baseLink}#mainmenu`;
        }

        this.#defineFavIcon();

        if (this.env) {
            document.body.classList.add(this.env);
        }

        this.#core.errorOccurred.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
            this.#toastMessage.addMessage({
                text: this.#translate.get('DOMAIN_BAAS_CORE.GENERIC_ERROR'),
                type: 'error'
            });
        });

        this.layout.sidebar$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((sidebar) => {
            this.#internalLayout.queueAction(() => {
                this.sidebar = sidebar;
            });
        });

        this.layout.title$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((title) => {
            this.#internalLayout.queueAction(() => {
                this.title = title;
            });
        });

        this.layout.subTitle$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((subTitle) => {
            this.#internalLayout.queueAction(() => {
                this.subTitle = subTitle;
            });
        });

        this.layout.loading$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((loading) => {
            this.#internalLayout.queueAction(() => {
                this.loading.set(loading);
            });
        });

        this.layout.mobileFriendly$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((mobileFriendly) => {
            this.#internalLayout.queueAction(() => {
                this.mobileFriendly = mobileFriendly;
            });
        });

        this.layout.touch$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((touch) => {
            const bodyTouch = document.body.classList.contains('touch');
            if (touch && !bodyTouch) document.body.classList.add('touch');
            else if (!touch && bodyTouch) document.body.classList.remove('touch');

            this.#internalLayout.queueAction(() => {
                this.touch = touch;
            });
        });

        this.layout.compact$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((compact) => {
            this.#internalLayout.queueAction(() => {
                this.compact = compact;
            });
        });

        this.layout.appMenuItems$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((appMenuItems) => {
            this.#internalLayout.queueAction(() => {
                this.appMenuItems = appMenuItems;
                this.#processAppMenuItems();
            });
        });

        this.layout.offcanvasOpened$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((offcanvasOpened) => {
            this.#internalLayout.queueAction(() => {
                this.offcanvasOpened.set(offcanvasOpened);
            });
        });

        this.layout.offcanvasPinned$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((offcanvasPinned) => {
            this.#internalLayout.queueAction(() => {
                this.offcanvasPinned.set(offcanvasPinned);
            });
        });

        this.layout.offcanvasOptions$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((offcanvasOptions) => {
            this.offcanvasOptions = offcanvasOptions;
        });

        this.#internalLayout.injectSection.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((injection) => {
            injection.componentRef = this.#injectSection(injection);
        });

        this.#internalLayout.clearSection.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((section) => {
            this.#clearSection(section);
        });

        this.#gvk.init();

        let firstLoad = true;
        this.layout.appLoading$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
            // on first load don't do anything and let standard flow handle it
            if (!firstLoad) {
                this.#defineAppLoading(value);
            } else firstLoad = false;
        });

        this.#initBreakpointObserver();
    }

    get mobileItems(): BarAppMenuItem[] {
        if (!this.mobileAppMenuItems) return [];
        if (this.mobileAppMenuItems.length < 2) {
            return [];
        } else if (this.mobileAppMenuItems.length < 6) {
            return this.mobileAppMenuItems;
        } else {
            return [
                ...this.mobileAppMenuItems.slice(0, 4),
                { content: 'Meer', icon: { srText: 'Meer', position: 'top', class: 'fas fa-ellipsis-h' }, action: (e) => (this.menuOverlayOpen = true) }
            ];
        }
    }

    get restOfMobileItems(): BarAppMenuItem[] {
        return this.mobileAppMenuItems.slice(4);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.appMenuItems) {
            this.#processAppMenuItems();
        }
    }
    ngOnDestroy(): void {
        this.#internalLayout.legacyConsumer = false;
    }

    navigateHome(): boolean {
        // if not home url override (disconnected modus) we use the core functionality to navigate
        if (this.#layoutState.homeUrl == null) {
            this.#core.navigateHome();
            return false;
        }
        return true;
    }

    navigateRoot(event: MouseEvent): boolean {
        this.#core.navigateRoot(true, event);
        return false;
    }

    defineAppMenuLinkClass(item: BarAppMenuItem): string {
        return item.selected ? 'menu-lv__item menu-lv__item active' : 'menu-lv__item';
    }

    exitCompact() {
        this.layout.compact$.next(false);
    }

    protected closeOffcanvas(): void {
        this.layout.offcanvasOpened$.next(false);
        this.layout.offcanvasPinned$.next(false);
    }

    // injects a component into a section of the layout
    #injectSection<T>(injection: BarLayoutSectionInjection<T>): ComponentRef<T> {
        // create a factory for the component (component maker) or use factory if one has been given
        // clear the content within the anchor
        this.#clearSection(injection.section);
        const sectionAnchor = this.#getSectionAnchor(injection.section);
        if (sectionAnchor) {
            // inject the component into the anchor
            const componentRef = this.#getSectionAnchor(injection.section).viewContainerRef.createComponent<T>(injection.component);
            componentRef.changeDetectorRef.detectChanges();
            return componentRef;
        }

        return undefined;
    }

    // clears a section within the layout
    #clearSection(section: BarLayoutSection): void {
        const sectionAnchor = this.#getSectionAnchor(section);
        if (sectionAnchor) sectionAnchor.viewContainerRef.clear();
    }

    // gets the directive for injection
    #getSectionAnchor(section: BarLayoutSection): _BarLayoutAnchorDirective {
        let anchors = [...this.barLayoutAnchors()];
        // NOTE null in hidden modus
        const pageComponent = this.pageComponent();
        if (pageComponent) {
            anchors = [...anchors, ...pageComponent.barLayoutPageAnchors(), ...pageComponent.barLayoutMenuComponent().barLayoutMenuAnchors()];
        }
        return anchors.find((directive) => directive.section() == section);
    }

    #defineFavIcon(): void {
        if (this.#appState != null) {
            const url = this.#appState.location.baasStatic.url + BAR_LAYOUT_CONSTANTS.IMAGES.FAVICON;
            const element = document.getElementById('favicon');
            element.setAttribute('href', url);
        }
        // NOTE in case disconnected (no state) the user must just provide default fav icon in the root folder
    }

    /** Defines the app loading whereby the app loader appears. */
    #defineAppLoading(value: boolean, firstLoad = false): void {
        const execute = () => {
            this.#internalLayout.appLoadingPending$$.next(true);

            const complete = () => {
                this.#internalLayout.appLoadingPending$$.next(false);
                if (firstLoad) this.#internalLayout.firstAppLoading$$.next(false);
            };

            const loader = document.getElementById('app-loading');
            if (loader) {
                const appRootElements = document.getElementsByTagName('app-root');
                const appRoot = appRootElements.length ? (appRootElements[0] as HTMLElement) : null;

                if (firstLoad && this.#internalLayout.lastAppLoadingRequest) {
                    console.info('Request for app loader to stay open.');
                    // still in first load but a last request came in to have it stayting open, dont't proceed further
                    // stop the subsequent app loading calls (last one wins)
                    this.#internalLayout.firstAppLoading$$.observers.forEach((obs) => obs.complete());
                    complete();
                } else {
                    if (!value) {
                        loader.setAttribute('class', 'app-loading-fade-out');
                        if (appRoot) appRoot.removeAttribute('style');
                        // after the animation of 500ms we remove the loader as it stack above the content and is no longer needed
                        // NOTE run outside ngzone to avoid change detection cycle
                        this.#window.setTimeoutOutsideAngular(() => {
                            setTimeout(() => {
                                loader.style.display = 'none';
                                loader.removeAttribute('class');
                                document.body.style.overflow = 'unset';
                                complete();
                            }, 500);
                        });
                    } else {
                        loader.removeAttribute('class');
                        loader.style.display = '';
                        // hide app root because it is rendered underneath the app-loading
                        if (appRoot) appRoot.style.display = 'none';
                        complete();
                    }
                }
            } else {
                // no loader just make sure everything is false
                complete();
            }
        };
        if (firstLoad) {
            // on first load we just execute the code
            execute();
        } else {
            // remember last requested value
            this.#internalLayout.lastAppLoadingRequest = value;
            // wait for firstload to be finished
            const firstAppLoadingComplete$ = this.#internalLayout.firstAppLoading$$.pipe(
                skipWhile((firstLoad) => firstLoad),
                take(1)
            );
            // and also wait in case app loading has already been invoked and it is not done yet
            const appLoadingComplete$ = this.#internalLayout.appLoadingPending$$.pipe(
                skipWhile((loading) => loading),
                take(1)
            );
            forkJoin([firstAppLoadingComplete$, appLoadingComplete$])
                .pipe(takeUntilDestroyed(this.#destroyRef))
                .subscribe(() => {
                    execute();
                });
        }
    }

    #processAppMenuItems(): void {
        this.desktopAppMenuItems = this.appMenuItems
            ? this.appMenuItems.filter((ami) => !ami.hidden && (ami.mobile == null || ami.mobile === false))
            : undefined;
        this.mobileAppMenuItems = this.appMenuItems
            ? [...this.appMenuItems.filter((ami) => !ami.hidden && (ami.mobile == null || ami.mobile === true))]
            : undefined;
    }

    #initBreakpointObserver(): void {
        const responsive = this.responsive();
        if (!responsive?.enabled) return;

        const mobileMaxWidth = responsive.mobileWidth || 768;
        const tabletMaxWidth = responsive.tabletWidth || 991;
        if (tabletMaxWidth <= mobileMaxWidth)
            console.error(`Tablet width (${tabletMaxWidth} px) cannot be smaller or equal to the mobile width (${mobileMaxWidth} px).`);
        const tabletMinWidth = mobileMaxWidth + 1;

        const mobileWidthExpression = `(max-width: ${mobileMaxWidth}px)`;
        const tabletWidthExpression = `(min-width: ${tabletMinWidth}px) and (max-width: ${tabletMaxWidth}px)`;

        this.#breakpointObserver
            .observe([mobileWidthExpression, tabletWidthExpression])
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((result) => {
                const [isMobile, isTablet] = [result.breakpoints[mobileWidthExpression], result.breakpoints[tabletWidthExpression]];

                const deviceType = isMobile ? BarDeviceType.Mobile : isTablet ? BarDeviceType.Tablet : BarDeviceType.Desktop;
                this.#internalLayout.deviceType$$.next(deviceType);
                const touch = isMobile || isTablet;
                this.layout.touch$.next(touch);
            });
    }
}
